import { useParams, useNavigate } from "react-router-dom";
import { getArticleBySlug, articleList } from "@/mocks/articles";
import { useEffect } from "react";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://pandabuy.guru";

function buildArticleJsonLd(article: ReturnType<typeof getArticleBySlug>) {
  if (!article) return null;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${siteUrl}/article/${article.slug}#article`,
        "headline": article.title,
        "description": article.subtitle,
        "image": article.imageUrl,
        "datePublished": "2026-06-29",
        "dateModified": "2026-06-29",
        "author": {
          "@type": "Organization",
          "name": "Pandabuy.guru",
          "url": siteUrl,
        },
        "publisher": {
          "@type": "Organization",
          "name": "Pandabuy.guru",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
          },
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}/article/${article.slug}`,
        },
        "inLanguage": "en",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl,
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": article.title,
            "item": `${siteUrl}/article/${article.slug}`,
          },
        ],
      },
    ],
  };
}

export default function DynamicArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleJsonLd = article ? buildArticleJsonLd(article) : null;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
          <a
            href="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-line"></i> Back to Home
          </a>
        </div>
      </main>
    );
  }

  const allSlugs = articleList.map((a) => a.slug);
  const currentIndex = allSlugs.indexOf(article.slug);
  const prevSlug = currentIndex > 0 ? allSlugs[currentIndex - 1] : null;
  const nextSlug = currentIndex < allSlugs.length - 1 ? allSlugs[currentIndex + 1] : null;

  return (
    <main className="min-h-screen bg-background-50">
      {articleJsonLd && <JsonLd data={articleJsonLd} />}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="section-container flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xl font-heading font-extrabold text-foreground-950 whitespace-nowrap">
              pandabuy<span className="text-primary-500">.guru</span>
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i>Home
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              Pandabuy Sheet
            </a>
          </div>
        </div>
      </nav>

      <article className="section-container py-10 md:py-14">
        <div className="max-w-[800px] mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">Updated June 2026</span>
              <span className="text-xs text-foreground-400">· {article.readTime}</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>

            <div className="mt-6 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-[280px] md:h-[380px] object-cover object-center"
              />
            </div>

            <p className="mt-8 text-base md:text-lg text-foreground-700 leading-relaxed">
              {article.excerpt}
            </p>
          </div>

          <div className="mb-10 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-600 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>

          <div className="mb-12 p-6 rounded-2xl bg-background-100 border border-background-200/60">
            <h2 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Table of Contents</h2>
            <ol className="space-y-2 text-sm">
              {article.sections.map((sec, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-primary-500 font-semibold flex-shrink-0">{i + 1}.</span>
                  <a href={`#section-${i}`} className="text-foreground-700 hover:text-primary-500 transition-colors">
                    {sec.heading}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {article.sections.map((sec, i) => (
            <section key={i} id={`section-${i}`} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                {i + 1}. {sec.heading}
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                {sec.paragraphs.map((p, pi) => (
                  <p key={pi}>{p}</p>
                ))}
              </div>
            </section>
          ))}

          <div className="mt-12 p-8 rounded-2xl bg-primary-50 border border-primary-200/50 text-center">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
              Ready to Start Exploring?
            </h3>
            <p className="text-foreground-600 mb-6 max-w-[480px] mx-auto text-sm md:text-base">
              Browse our complete pandabuy spreadsheet database — organized by category, verified by the community, and updated weekly.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600 hover:translate-y-[-1px]"
              >
                <i className="ri-home-line"></i>
                Back to Homepage
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 bg-accent-500 text-background-50 hover:bg-accent-600 hover:translate-y-[-1px]"
              >
                Pandabuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-background-200/60">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                {prevSlug ? (
                  <a
                    href={`/article/${prevSlug}`}
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Previous Article
                  </a>
                ) : (
                  <span className="text-sm text-foreground-300 flex items-center gap-1">
                    <i className="ri-arrow-left-line"></i>
                    Previous Article
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-foreground-400">Share this guide:</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors text-sm">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors text-sm">
                  <i className="ri-facebook-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors text-sm">
                  <i className="ri-reddit-line"></i>
                </a>
              </div>
              <div>
                {nextSlug ? (
                  <a
                    href={`/article/${nextSlug}`}
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1"
                  >
                    Next Article
                    <i className="ri-arrow-right-line"></i>
                  </a>
                ) : (
                  <span className="text-sm text-foreground-300 flex items-center gap-1">
                    Next Article
                    <i className="ri-arrow-right-line"></i>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>

      <footer className="w-full bg-secondary-100 py-8">
        <div className="section-container text-center">
          <a href="/" className="inline-block mb-3">
            <span className="text-lg font-heading font-extrabold text-foreground-950 whitespace-nowrap">
              pandabuy<span className="text-primary-500">.guru</span>
            </span>
          </a>
          <p className="text-xs text-foreground-500">
            &copy; 2026 pandabuy.guru. All rights reserved. This site is an independent guide and is not affiliated with Pandabuy.
          </p>
        </div>
      </footer>
    </main>
  );
}